import { toast } from "sonner";
import {
  useCreateYawCheck,
  useUpdateYawCheck,
  useSignOffYawCheck,
  useUnlockYawCheck,
  useDeleteYawCheck,
} from "@/api/hooks";
import type { BuildId, YawCheckId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type YawCheck = components["schemas"]["YawCheckResponse"];
type YawCheckCreate = components["schemas"]["YawCheckCreate"];
type YawCheckUpdate = components["schemas"]["YawCheckUpdate"];

export interface YawCheckActions {
  /** Resolves on success; rejects on failure so callers can keep their UI open for retry. */
  handleCreate: (data: YawCheckCreate) => Promise<void>;
  /** Resolves on success; rejects on failure so callers can keep their UI open for retry. */
  handleUpdate: (id: YawCheckId, data: YawCheckUpdate) => Promise<void>;
  handleSignOff: (id: YawCheckId) => void;
  handleUnlock: (id: YawCheckId) => void;
  handleDelete: (id: YawCheckId) => void;
  isCreating: boolean;
  isUpdating: boolean;
  isSigningOff: boolean;
  isUnlocking: boolean;
  isDeleting: boolean;
  yawChecks: YawCheck[];
  signedOffCount: number;
}

export function useYawCheckActions(
  buildId: BuildId,
  yawChecks: YawCheck[] | undefined,
): YawCheckActions {
  const create = useCreateYawCheck(buildId);
  const update = useUpdateYawCheck(buildId);
  const signOff = useSignOffYawCheck(buildId);
  const unlock = useUnlockYawCheck(buildId);
  const del = useDeleteYawCheck(buildId);

  const checks = yawChecks ?? [];
  const signedOffCount = checks.filter((yc) => yc.signed_off_at).length;

  const handleCreate = (data: YawCheckCreate) =>
    create.mutateAsync(data).then(
      () => { toast.success("YAW check recorded"); },
      (err: Error) => { toast.error(err.message); throw err; },
    );

  const handleUpdate = (id: YawCheckId, data: YawCheckUpdate) =>
    update.mutateAsync({ checkId: id, data }).then(
      () => { toast.success("YAW check updated"); },
      (err: Error) => { toast.error(err.message); throw err; },
    );

  const handleSignOff = (id: YawCheckId) => {
    signOff.mutate(id, {
      onSuccess: () => toast.success("YAW check signed off"),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleUnlock = (id: YawCheckId) => {
    unlock.mutate(id, {
      onSuccess: () => toast.success("YAW check unlocked"),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleDelete = (id: YawCheckId) => {
    del.mutate(id, {
      onSuccess: () => toast.success("YAW check deleted"),
      onError: (err) => toast.error(err.message),
    });
  };

  return {
    handleCreate,
    handleUpdate,
    handleSignOff,
    handleUnlock,
    handleDelete,
    isCreating: create.isPending,
    isUpdating: update.isPending,
    isSigningOff: signOff.isPending,
    isUnlocking: unlock.isPending,
    isDeleting: del.isPending,
    yawChecks: checks,
    signedOffCount,
  };
}
